import { APIClient, ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import { Token } from '@frontend/authentication-v2';
import {
    AggregatedStockListResponse,
    AggregatedStockQueryParams,
    CreateStock,
    GroupedAggregatedStockListResponse,
    GroupedAggregatedStockQueryParams,
    Stock,
    StockListResponse,
    StockQueryParams,
    UpdateStock
} from '@frontend/stock/types';

export class StockClient extends APIClient {
    public static readonly endpoint = '/stock-api/v1';

    public static async fetchStocks(queryParams?: ApiQueryParams<StockQueryParams>): Promise<StockListResponse> {
        return await this.apiPaginated<StockListResponse, DefaultQueryParams | StockQueryParams>(`${this.endpoint}/stocks`, queryParams);
    }

    public static async resolveStocks(stockIds: string[], field: string = 'id'): Promise<StockListResponse> {
        const response = await this.post(`${this.endpoint}/stocks-resolve`, { ids: stockIds, field });
        return await this.handleResponse<StockListResponse>(response);
    }

    public static async deleteStocks(stockIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/stocks-delete`, { ids: stockIds });
        return await this.handleVoidResponse(response);
    }

    public static async postStock(accountId: string, spotId: string, moduleId: string, slotId: string, stock: CreateStock): Promise<Stock> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/spots/${spotId}/modules/${moduleId}/slots/${slotId}/stocks`, stock);
        return await this.handleResponse<Stock>(response);
    }

    public static async fetchSlotStock(accountId: string, spotId: string, moduleId: string, slotId: string, stockId: string): Promise<Stock> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/spots/${spotId}/modules/${moduleId}/slots/${slotId}/stocks/${stockId}`);
        return await this.handleResponse<Stock>(response);
    }

    public static async patchStock(accountId: string, spotId: string, moduleId: string, slotId: string, stockId: string, body: UpdateStock): Promise<Stock> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/spots/${spotId}/modules/${moduleId}/slots/${slotId}/stocks/${stockId}`, body);
        return await this.handleResponse<Stock>(response);
    }

    public static async deleteStock(
        accountId: string,
        spotId: string,
        moduleId: string,
        slotId: string,
        stockId: string,
        includeAccessToken?: boolean,
        withCredentials?: Token
    ): Promise<void> {
        const response = await this.delete(
            `${this.endpoint}/accounts/${accountId}/spots/${spotId}/modules/${moduleId}/slots/${slotId}/stocks/${stockId}`,
            includeAccessToken,
            withCredentials
        );
        return await this.handleVoidResponse(response);
    }

    public static async fetchLockedStocks(queryParams?: ApiQueryParams<StockQueryParams>): Promise<StockListResponse> {
        return await this.apiPaginated<StockListResponse, StockQueryParams>(`${this.endpoint}/locked-stocks`, queryParams);
    }

    public static async fetchAggregatedStocks(
        accountId: string,
        spotId: string,
        queryParams?: ApiQueryParams<AggregatedStockQueryParams>
    ): Promise<AggregatedStockListResponse> {
        return await this.apiPaginated<AggregatedStockListResponse, AggregatedStockQueryParams>(
            `${this.endpoint}/accounts/${accountId}/spots/${spotId}/aggregated-stocks`,
            queryParams
        );
    }

    public static async fetchGroupedAggregatedStocks(
        accountId: string,
        spotId: string,
        queryParams?: ApiQueryParams<GroupedAggregatedStockQueryParams>
    ): Promise<GroupedAggregatedStockListResponse> {
        return await this.apiPaginated<GroupedAggregatedStockListResponse, GroupedAggregatedStockQueryParams>(
            `${this.endpoint}/accounts/${accountId}/spots/${spotId}/grouped-aggregated-stocks`,
            queryParams
        );
    }

    public static async fetchAllGroupedAggregatedStocks(
        accountId: string,
        queryParams?: ApiQueryParams<GroupedAggregatedStockQueryParams>
    ): Promise<GroupedAggregatedStockListResponse> {
        return await this.apiPaginated<GroupedAggregatedStockListResponse, GroupedAggregatedStockQueryParams>(
            `${this.endpoint}/accounts/${accountId}/grouped-aggregated-stocks`,
            queryParams
        );
    }

    public static async postIoTSeed(accountId: string, iotId: string, body: { account_id: string }): Promise<void> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/iots/${iotId}/seed`, body);
        return this.handleResponse(response);
    }
}
